/**
 * What the pages need from where they are rendered, and nothing else: how to
 * draw a link (the app's router link, or a plain `<a>` in a static snapshot —
 * same markup either way), whether the visitor is signed in, the time "now"
 * is, and genre names. Passed in, never read from the browser, so a server
 * render is deterministic.
 */
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

export interface SiteLinkProps {
  href: string;
  className?: string;
  children?: ReactNode;
  'aria-label'?: string;
  'aria-current'?: 'page';
}

export type SiteLinkComponent = (props: SiteLinkProps) => ReactElement;

export interface GamesSiteContextValue {
  Link: SiteLinkComponent;
  /** The visitor is signed in (Play needs no sign-in step). */
  signedIn: boolean;
  /** Milliseconds since the epoch — what "new" and the footer year are measured from. */
  now: number;
  /** Genre id → its name. */
  genreLabels: Readonly<Record<string, string>>;
  /** App category id → its name (the Arche Store). */
  categoryLabels: Readonly<Record<string, string>>;
  /**
   * The new Arche Store is shown here, so the public frame links to it. Off,
   * no link to a store that is not there.
   */
  storeShown: boolean;
  /**
   * The Games page is shown here, so the public frame links to it. On by
   * default (the Games page's own frame); the Arche Store passes its switch,
   * so a store page never links to a Games page that is off.
   */
  gamesShown: boolean;
}

export function PlainLink({ href, children, ...rest }: SiteLinkProps): ReactElement {
  return (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}

const DEFAULTS: GamesSiteContextValue = {
  Link: PlainLink,
  signedIn: false,
  now: 0,
  genreLabels: {},
  categoryLabels: {},
  storeShown: false,
  gamesShown: true,
};

const GamesSiteContext = createContext<GamesSiteContextValue>(DEFAULTS);

export function GamesSiteProvider({
  children,
  ...value
}: Partial<GamesSiteContextValue> & { children: ReactNode }): ReactElement {
  return (
    <GamesSiteContext.Provider value={{ ...DEFAULTS, ...value }}>
      {children}
    </GamesSiteContext.Provider>
  );
}

export function useGamesSite(): GamesSiteContextValue {
  return useContext(GamesSiteContext);
}

/** A genre's name, or its id made readable when the name is not known. */
export function genreName(labels: Readonly<Record<string, string>>, id: string): string {
  return labels[id] ?? id.charAt(0).toUpperCase() + id.slice(1);
}

/** A category's name, or its id made readable when the name is not known. */
export function categoryName(labels: Readonly<Record<string, string>>, id: string): string {
  return (
    labels[id] ??
    id
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}
